import { addListener, createSelector } from '@reduxjs/toolkit';
import {
  checkSingleBetExceededLimitSelector,
  ExceedState,
  getBetLimitBySportTypeSelector,
  getMatchesByMatchIds,
  RootState,
  selectCartStep,
  selectIsAnySingleBetError,
  touchSingleBetError,
  updateSingleBetAmount,
  useAppDispatch,
  useAppSelector,
} from '@supersports/services';
import { useEffect, useRef } from 'react';

const selectSingleBetMatchIds = createSelector(
  (state: RootState) => state.cart.cart.singleBet ?? [],
  (betArgs) => betArgs.map((bet) => bet.Match.MatchID)
);

/**
 * A custom React hook that periodically fetches match data for single bets.
 *
 * @function useSingleBetPolling
 *
 * @example
 * useSingleBetPolling();
 *
 * @returns {void} This hook does not return any values. Its sole purpose is to set up the polling mechanism when the component mounts and clean it up when the component unmounts or the dependencies change.
 */
export const useSingleBetPolling = () => {
  const matchIds = useAppSelector(selectSingleBetMatchIds);
  const dispatch = useAppDispatch();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const currentStep = useAppSelector(selectCartStep);

  useEffect(() => {
    if (matchIds.length > 0 && currentStep === 'BETTING') {
      intervalRef.current = setInterval(() => {
        console.log('Polling for single bet');
        dispatch(getMatchesByMatchIds({ matchIds, singleCartPolling: true }));
      }, 5000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [matchIds, currentStep]);
};

export const getSingleBetPayloadSelector = createSelector(
  (state: RootState) => state.cart.cart.singleBet,
  (_: RootState, { id }: { id: string }) => id,
  (singleBet, singleBetSelectedId) => {
    return singleBet.find((item) => item.BetArgs.id! === singleBetSelectedId);
  }
);

export const selectParlayBetList = createSelector(
  (state: RootState) => state.cart.cart.parlayBet,
  (parlayBet) => parlayBet?.BetArgs ?? []
);
export const checkParlayBetExceededLimitSelector = createSelector(
  selectParlayBetList,
  (state: RootState) => getBetLimitBySportTypeSelector(state, { sportType: 'PAR' }),
  (parlayBet, betLimit) => {
    if (!parlayBet || parlayBet.length === 0 || !betLimit) return ExceedState.NotExceeded;
    const betAmount = parlayBet[0].BetAmt;
    const totalBetAmount = parlayBet.reduce((acc, cur) => acc + Number(cur.BetAmt), 0);
    if (betAmount < betLimit.MinBetLimit) return ExceedState.LessThanMin;
    if (betAmount > betLimit.MaxBetLimit) return ExceedState.MoreThanMax;
    if (totalBetAmount > betLimit.MaxBetMatch) return ExceedState.MoreThanMax;
    return ExceedState.NotExceeded;
  }
);

export const useSingleBetValidator = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        actionCreator: updateSingleBetAmount,
        effect: ({ payload }, { getState, dispatch }) => {
          const isValid =
            checkSingleBetExceededLimitSelector(getState() as RootState, {
              id: payload.BetArgs.id!,
              sportType: payload.Match.SportType,
            }) === ExceedState.NotExceeded;
          dispatch(touchSingleBetError({ id: payload.BetArgs.id!, isError: !isValid }));
        },
      })
    );

    return () => {
      dispatch(unsubscribe);
    };
  }, []);

  const isAnySingleBetError = useAppSelector(selectIsAnySingleBetError);

  return { isAnySingleBetError };
};
